import React from "react";
import { useSelector } from "react-redux";
import {
  FaClock,
  FaMapMarkerAlt,
  FaPhone,
  FaWhatsapp,
  FaLaptop,
} from "react-icons/fa";
import { formatTime, getNextOpeningTime } from "../../utils/storeTimeUtils";
import useBackgroundImage from "../../hooks/useBackgroundImage";
import { getFullScreenBackgroundStyle } from "../../utils/backgroundStyles";

const StoreClosed = () => {
  const { profile } = useSelector((state) => state.profile);
  const backgroundImage = useBackgroundImage();

  const nextOpening = getNextOpeningTime(profile?.from_time);
  const timeUntilOpening = nextOpening
    ? Math.ceil((nextOpening - new Date()) / (1000 * 60 * 60))
    : null;

  // Check if store is unavailable due to status being false
  const isStoreUnavailable = profile?.status === false;

  return (
    <div
      className="min-h-screen relative overflow-y-auto overflow-x-hidden store-closed-container"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/70"></div>

      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-5 w-48 h-48 bg-primary/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-10 right-5 w-64 h-64 bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-16 md:py-8">
        <div className="max-w-xl mx-auto text-center w-full">
          {/* Store Logo and Name */}
          <div className="mb-6 mt-8 md:mt-0">
            {/* {profile?.DocLogo?.imageUrl && (
              <div className="mb-4">
                <img 
                  src={profile.DocLogo.imageUrl} 
                  alt={profile?.name || 'Restaurant Logo'} 
                  className="w-20 h-20 mx-auto object-contain rounded-xl shadow-xl border-2 border-white/30"
                />
              </div>
            )} */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 poppins drop-shadow-lg">
              {profile?.name || "Restaurant"}
            </h1>
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 border-2 border-red-400 rounded-full backdrop-blur-sm shadow-lg transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center">
                <FaLaptop className="w-5 h-5 text-white mr-3 animate-pulse" />
                <span className="text-white font-bold text-lg poppins tracking-wide uppercase">
                  {isStoreUnavailable
                    ? "Currently Store Not Available"
                    : "Currently Closed"}
                </span>
                <div className="ml-3 flex space-x-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse delay-150"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse delay-300"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Opening Hours - Only show when store is not unavailable */}
          {!isStoreUnavailable && (
            <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 mb-6 border border-gray-300 shadow-lg">
              <div className="flex items-center justify-center mb-4">
                <FaClock className="w-6 h-6 text-yellow-600 mr-3" />
                <h2 className="text-xl font-bold text-gray-800 poppins">
                  Opening Hours
                </h2>
              </div>

              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-gray-800 mb-1 poppins">
                  {formatTime(profile?.from_time)} -{" "}
                  {formatTime(profile?.to_time)}
                </div>
                <p className="text-gray-600 text-sm poppins">Daily</p>
              </div>

              {timeUntilOpening && (
                <div className="bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded-xl p-4 border border-yellow-500/50">
                  <p className="text-gray-700 text-sm mb-1 poppins font-semibold">
                    We'll be back in
                  </p>
                  <div className="text-2xl font-bold text-yellow-700 poppins">
                    {timeUntilOpening} hour{timeUntilOpening !== 1 ? "s" : ""}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Address */}
            <div className="bg-white/90 backdrop-blur-md rounded-xl p-4 border border-gray-300 shadow-lg">
              <div className="flex items-center mb-3">
                <FaMapMarkerAlt className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-800 poppins">
                  Visit Us
                </h3>
              </div>
              <p className="text-gray-700 text-sm poppins leading-relaxed">
                {profile?.address || "Address not available"}
              </p>
            </div>

            {/* Contact */}
            <div className="bg-white/90 backdrop-blur-md rounded-xl p-4 border border-gray-300 shadow-lg">
              <div className="flex items-center mb-3">
                <FaPhone className="w-5 h-5 text-green-600 mr-2 transform rotate-90" />
                <h3 className="text-lg font-semibold text-gray-800 poppins">
                  Contact
                </h3>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() =>
                    window.open(`tel:${profile?.mobile}`, "_blank")
                  }
                  className="flex items-center justify-center w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-lg text-white transition-all duration-300 hover:scale-105 poppins text-sm font-semibold"
                >
                  <FaPhone className="w-3 h-3 mr-2 transform rotate-90" />
                  Call Us
                </button>
                <button
                  onClick={() => {
                    const message =
                      " Hello 👋,I visited your website and saw that you’re currently closed. I’d like to inquire about. Could you please share the details when you’re available? Looking forward to your response. Thank you! 🙏";
                    const url = `https://wa.me/${
                      profile?.whatsapp
                    }?text=${encodeURIComponent(message)}`;
                    window.open(url, "_blank");
                  }}
                  className="flex items-center justify-center w-full px-3 py-2 bg-green-600 hover:bg-green-700 border border-green-600 rounded-lg text-white transition-all duration-300 hover:scale-105 poppins text-sm font-semibold"
                >
                  <FaWhatsapp className="w-3 h-3 mr-2" />
                  WhatsApp
                </button>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="bg-white/90 backdrop-blur-md rounded-xl p-4 border border-gray-300 shadow-lg mb-8">
            <p className="text-gray-700 text-sm poppins leading-relaxed font-medium">
              {isStoreUnavailable
                ? "We're currently not available for orders. Please check back later or contact us for more information. We apologize for any inconvenience."
                : "We're currently closed . For reservations, catering inquiries, or special orders, please contact us during our business hours or leave us a message. We look forward to serving you soon!"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreClosed;
